import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import type { Hit, SourceResult } from "../api/client";
import { formatDateTime, percent } from "../format";
import { icons } from "../icons";
import { badgeClass, stateView } from "../signal-state";
import { shared } from "../styles/shared";

const BIRTH = {
  match: { label: "Naissance concordante", tone: "red" },
  mismatch: { label: "Naissance différente", tone: "green" },
  unknown: { label: "Naissance non comparée", tone: "neutral" },
} as const;

/** Collapsible details of one signal. */
@customElement("ld-voyant-section")
export class LdVoyantSection extends LitElement {
  @property({ attribute: false }) result!: SourceResult;
  @property({ type: Number }) index = 0;
  @property({ type: Boolean, reflect: true }) open = false;

  static styles = [
    shared,
    css`
      :host { display: block; scroll-margin-top: calc(var(--header-h) + 16px); }
      .section { border: 1px solid var(--slate-200); border-radius: var(--r-lg); margin-bottom: 18px; overflow: hidden; background: var(--white); }
      .head {
        width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px;
        padding: 20px 24px; background: none; border: 0; text-align: left; color: inherit;
      }
      .head:hover { background: var(--slate-100); }
      .left { display: flex; align-items: center; gap: 16px; min-width: 0; }
      .num {
        font-family: var(--font-mono); font-size: 12px; color: var(--slate-400); font-weight: 600;
        width: 26px; height: 26px; border-radius: 50%; background: var(--slate-100);
        display: flex; align-items: center; justify-content: center; flex-shrink: 0;
      }
      .titles h3 { font-size: 15.5px; font-weight: 700; }
      .titles span { font-size: 12.5px; color: var(--slate-400); font-weight: 500; }
      .right { display: flex; align-items: center; gap: 14px; }
      .chevron { color: var(--slate-400); transition: transform .2s var(--ease); }
      .chevron svg { width: 18px; height: 18px; }
      :host([open]) .chevron { transform: rotate(180deg); }
      @media (max-width: 640px) {
        .head { flex-wrap: wrap; padding: 16px; gap: 10px; }
        .left { flex: 1 1 100%; gap: 12px; }
        .titles span { display: none; }
        .right { margin-left: 38px; }
        .body { padding: 4px 16px 18px !important; }
      }
      .body { padding: 4px 24px 24px; }
      .body > p { font-size: 13.5px; color: var(--slate-600); line-height: 1.65; margin-bottom: 14px; max-width: 640px; }
      .detail-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 12px 0 18px; }
      @media (max-width: 720px) { .detail-grid { grid-template-columns: 1fr 1fr; } }
      .detail { border: 1px solid var(--slate-200); border-radius: var(--r-sm); padding: 12px 14px; }
      .detail .k { font-size: 11px; color: var(--slate-400); font-weight: 600; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 4px; }
      .detail .v { font-size: 13.5px; font-weight: 600; }
      .hits { border: 1px solid var(--slate-200); border-radius: var(--r-md); overflow: hidden; }
      .hit { display: grid; grid-template-columns: 1fr auto auto; gap: 14px; align-items: center; padding: 12px 16px; border-bottom: 1px solid var(--slate-200); }
      .hit:last-child { border-bottom: none; }
      @media (max-width: 640px) { .hit { grid-template-columns: 1fr auto; } .hit .badge { grid-column: 1 / -1; justify-self: start; } }
      .hit-name { font-weight: 600; font-size: 13.5px; display: inline-flex; align-items: center; gap: 6px; }
      .hit-name svg { width: 13px; height: 13px; color: var(--slate-400); }
      a.hit-name:hover { color: var(--blue-600); }
      .hit-meta { font-size: 12px; color: var(--slate-400); margin-top: 2px; }
      .score { font-family: var(--font-mono); font-size: 12.5px; font-weight: 600; color: var(--slate-600); }
      .note { margin: 0 0 14px; }
      .source-link { margin-top: 16px; }
    `,
  ];

  private toggle() {
    this.open = !this.open;
  }

  private renderHit(hit: Hit) {
    const birth = BIRTH[hit.birth];
    const name = hit.url
      ? html`<a class="hit-name" href=${hit.url} target="_blank" rel="noopener noreferrer">${hit.label} ${icons.external}</a>`
      : html`<span class="hit-name">${hit.label}</span>`;
    const meta = [hit.matched_on === "alias" ? "trouvé via un alias" : null, hit.details].filter(Boolean).join(" · ");
    return html`<div class="hit">
      <div>${name}${meta ? html`<div class="hit-meta">${meta}</div>` : nothing}</div>
      <span class="score" title="Similarité du nom">${percent(hit.score)}</span>
      <span class=${badgeClass(birth.tone)}>${birth.label}</span>
    </div>`;
  }

  render() {
    const r = this.result;
    const view = stateView(r.state);
    const best = r.hits.length ? Math.max(...r.hits.map((h) => h.score)) : null;
    const bodyId = `voyant-body-${r.source}`;
    return html`<div class="section">
      <button class="head" @click=${this.toggle} aria-expanded=${this.open} aria-controls=${bodyId}>
        <span class="left">
          <span class="num">${String(this.index + 1).padStart(2, "0")}</span>
          <ld-signal compact .code=${r.source} .state=${r.state}></ld-signal>
          <span class="titles"><h3>${r.source} — ${r.label}</h3><span>${view.description}</span></span>
        </span>
        <span class="right">
          <span class=${badgeClass(view.tone)}>${view.label}</span>
          <span class="chevron">${icons.chevron}</span>
        </span>
      </button>
      ${this.open
        ? html`<div class="body" id=${bodyId}>
            ${r.note ? html`<div class="alert alert-info note">${icons.info}<span>${r.note}</span></div>` : nothing}
            <div class="detail-grid">
              <div class="detail"><div class="k">Résultats</div><div class="v">${r.total}</div></div>
              <div class="detail"><div class="k">Meilleure similarité</div><div class="v">${best === null ? "—" : percent(best)}</div></div>
              <div class="detail">
                <div class="k">${r.source_version ? "Version des données" : "Consultée le"}</div>
                <div class="v">${formatDateTime(r.source_version ?? r.checked_at)}</div>
              </div>
            </div>
            ${r.hits.length
              ? html`<div class="hits">${r.hits.map((h) => this.renderHit(h))}</div>`
              : html`<p>${r.state === "clear" ? "Aucune fiche correspondante." : "Aucune fiche détaillée fournie par la source."}</p>`}
            ${r.search_url
              ? html`<a class="btn btn-secondary btn-sm source-link" href=${r.search_url} target="_blank" rel="noopener noreferrer">
                  ${icons.external} Ouvrir la recherche sur la source
                </a>`
              : nothing}
          </div>`
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ld-voyant-section": LdVoyantSection;
  }
}
